import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';

import { Helmet } from 'react-helmet-async';

import PageHeader from './PageHeader';
import CodeSample from '../CodeSample';
import { palette } from '../../theme';
import { useColorMode } from '../../colorMode';
import { SITE_URL } from '../../lib/seo';

// ─────────────────────────────────────────────────────────────
// Platform — flagship product page: Arabic-native multimodal
// city intelligence, five capabilities with honest status.
// ─────────────────────────────────────────────────────────────

const LIVE_STATUS = new Set(['Live', 'En production', 'مباشر']);

export default function Platform() {
  const { t } = useTranslation();
  const { mode } = useColorMode();
  const pillars = t('home.platform.items', { returnObjects: true }) || [];
  const sage = (palette[mode] || palette.light).sage;

  return (
    <Box>
      <Helmet>
        <title>Platform | Al-Amen Technology</title>
        <link rel="canonical" href={`${SITE_URL}/platform`} />
      </Helmet>
      <PageHeader
        eyebrow={t('platform.eyebrow')}
        title={t('platform.title')}
        body={t('platform.body')}
      />

      <Container maxWidth="xl" sx={{ pt: 3 }}>
        <Button
          component={RouterLink}
          to="/architecture"
          variant="text"
          endIcon={<ArrowForwardRoundedIcon />}
          sx={{ color: 'text.primary' }}
        >
          {t('platform.architectureCta')}
        </Button>
      </Container>

      {/* Pillar breakdown */}
      <Container maxWidth="xl" sx={{ py: { xs: 5, md: 8 } }}>
        <Stack spacing={3}>
          {pillars.map((p) => (
            <Paper
              key={p.id}
              id={p.id}
              variant="outlined"
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 3,
                bgcolor: 'background.paper',
                scrollMarginTop: 96
              }}
            >
              <Grid container spacing={4} alignItems="flex-start">
                <Grid item xs={12} md={4}>
                  <Typography
                    sx={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: '0.82rem',
                      color: 'text.secondary'
                    }}
                  >
                    {p.num}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'var(--font-serif)',
                      fontSize: { xs: '1.8rem', md: '2.1rem' },
                      letterSpacing: '-0.02em',
                      lineHeight: 1.1,
                      mt: 1
                    }}
                  >
                    {p.title}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
                    <Chip
                      label={p.product}
                      size="small"
                      sx={{ fontFamily: 'var(--font-mono)', bgcolor: 'primary.main', color: 'background.default' }}
                    />
                    {p.status ? (
                      <Chip
                        label={p.status}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontFamily: 'var(--font-mono)',
                          ...(LIVE_STATUS.has(p.status)
                            ? { borderColor: sage, color: sage }
                            : { borderColor: 'divider', color: 'text.secondary' })
                        }}
                      />
                    ) : null}
                  </Stack>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Typography sx={{ color: 'text.secondary', mb: 2, fontSize: '1.05rem', lineHeight: 1.6 }}>
                    {p.body}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                    {(p.tags || []).map((tag) => (
                      <Chip key={tag} label={tag} size="small" variant="outlined" />
                    ))}
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          ))}
        </Stack>

        <Typography
          sx={{
            fontFamily: 'var(--font-mono)',
            fontSize: '0.72rem',
            color: 'text.secondary',
            textAlign: 'center',
            mt: 4
          }}
        >
          {t('platform.pillarNote')}
        </Typography>
      </Container>

      {/* Developer surface */}
      <Box
        component="section"
        sx={{
          py: { xs: 5, md: 8 },
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper'
        }}
      >
        <Container maxWidth="xl">
          <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
            <Grid item xs={12} md={5}>
              <Typography
                sx={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '0.72rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.14em',
                  color: 'text.secondary',
                  mb: 1.5
                }}
              >
                {t('platform.sdkEyebrow')}
              </Typography>
              <Typography
                component="h2"
                sx={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: { xs: '2rem', md: '2.4rem' },
                  letterSpacing: '-0.02em',
                  lineHeight: 1.1,
                  mb: 2
                }}
              >
                {t('platform.sdkTitle')}
              </Typography>
              <Typography sx={{ color: 'text.secondary', maxWidth: 480, mb: 3 }}>
                {t('platform.sdkBody')}
              </Typography>
              <Button
                component={RouterLink}
                to={t('platform.cta.to')}
                variant="contained"
                endIcon={<ArrowForwardRoundedIcon />}
                sx={{
                  bgcolor: 'text.primary',
                  color: 'background.default',
                  borderRadius: 999,
                  '&:hover': { bgcolor: 'text.primary', opacity: 0.9 }
                }}
              >
                {t('platform.cta.label')}
              </Button>
            </Grid>
            <Grid item xs={12} md={7}>
              <CodeSample />
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}
